import type { GeoLocation } from "@repo/design-system/types/geo";
import type {
  GeoapifyFeature,
  GeoapifyFeatureCollection,
  GeoapifyObject,
} from "@repo/design-system/types/geoapify";
import { z } from "zod";

const APIKEY = process.env.GEOAPIFY_APIKEY;

export const apiSchema = z.object({
  type: z.enum(["geo_reverse", "place", "geo"]).default("geo"),
  lang: z.string().min(2).max(2),
});

export const geoSearchSchema = apiSchema.extend({
  type: z.enum(["geo"]),
  query: z.string(),
  customFilter: z.string().optional(),
});

export const reverseGeosearchSchema = apiSchema.extend({
  type: z.enum(["geo_reverse"]),
  lat: z.number(),
  lng: z.number(),
});

export const placeDetailsSchema = apiSchema.extend({
  type: z.enum(["place"]),
  placeId: z.string(),
});

export type Geosearch = z.infer<typeof geoSearchSchema>;
export type ReverseGeosearch = z.infer<typeof reverseGeosearchSchema>;
export type PlaceDetailsGeosearch = z.infer<typeof placeDetailsSchema>;
export type GeoApifyPayload =
  | Geosearch
  | ReverseGeosearch
  | PlaceDetailsGeosearch;
export type GeoApifyResponse<T> = T extends Geosearch | ReverseGeosearch
  ? GeoapifyObject
  : GeoapifyFeatureCollection;

export async function geoSearch(
  query: string,
  lang: string,
  customFilter?: string
) {
  if (!APIKEY) {
    throw new Error("MISSING ENV: GEOAPIFY_APIKEY");
  }

  const url = new URL("https://api.geoapify.com/v1/geocode/autocomplete");

  url.searchParams.set("apiKey", APIKEY);
  url.searchParams.set("lang", lang);
  url.searchParams.set("text", query);
  url.searchParams.set("type", "locality");
  url.searchParams.set("limit", "10");

  if (customFilter) {
    const params = new URLSearchParams(customFilter);
    for (const [key, value] of params.entries()) {
      url.searchParams.set(key, value);
    }
  }

  const res = await fetch(url.href);

  return handleResponse<GeoapifyObject>(res);
}

export async function reverseGeosearch(lat: number, lon: number, lang: string) {
  if (!APIKEY) {
    throw new Error("MISSING ENV: GEOAPIFY_APIKEY");
  }

  const url = new URL("https://api.geoapify.com/v1/geocode/reverse");

  url.searchParams.set("apiKey", APIKEY);
  url.searchParams.set("lang", lang);
  url.searchParams.set("lat", String(lat));
  url.searchParams.set("lon", String(lon));

  const res = await fetch(url.href);
  return handleResponse<GeoapifyObject>(res);
}

export async function getPlaceDetails(placeId: string, lang: string) {
  if (!APIKEY) {
    throw new Error("MISSING ENV: GEOAPIFY_APIKEY");
  }

  const url = new URL("https://api.geoapify.com/v2/place-details");

  url.searchParams.set("apiKey", APIKEY);
  url.searchParams.set("lang", lang);
  url.searchParams.set("id", placeId);
  url.searchParams.set("features", "details.full_geometry");

  const res = await fetch(url.href);
  return handleResponse<GeoapifyFeatureCollection>(res);
}

export function geoapifyToGeoLocation(feature: GeoapifyFeature): GeoLocation {
  const { properties } = feature;
  let name = properties.formatted;
  if (properties.city) {
    name = [properties.city, properties.state, properties.country]
      .filter(Boolean)
      .join(", ");
  }

  return {
    name,
    lat: feature.properties.lat,
    lng: feature.properties.lon,
    placeId: feature.properties.place_id,
  };
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    throw new Error("invalid_response");
  }

  return await res.json();
}
